import {
  P4ValidationStatus,
  RiskClassification,
  ScoringTrigger,
  type P4ValidationItem,
  type P4PendingCount,
} from "@/core/compliance/entities/P4Validation";

// ---------------------------------------------------------------------------
// API DTO shapes (private to the details/ layer)
// ---------------------------------------------------------------------------

export interface P4ValidationItemDto {
  validationId: string;
  businessKey: string;
  oldRiskClass: string;
  newRiskClass: string;
  oldScore: number;
  newScore: number;
  scoreDelta: number;
  triggerReason: string;
  sourceTrigger: string;
  status: string;
  slaDeadline: string;
  slaExpired: boolean;
  flaggedForDirectionRisques: boolean;
  createdAt: string;
}

/**
 * Compteur PENDING : le backend renvoie la valeur **brute** (`data: 3`). La forme objet
 * `{ count }` est tolérée par sécurité (contrat historique).
 */
export type P4PendingCountDto = number | { count: number };

// ---------------------------------------------------------------------------
// Mapping functions
// ---------------------------------------------------------------------------

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapP4Status(raw: string): P4ValidationStatus {
  switch (raw) {
    case "PENDING": return P4ValidationStatus.PENDING;
    case "APPROVED": return P4ValidationStatus.APPROVED;
    case "REJECTED": return P4ValidationStatus.REJECTED;
    case "SLA_EXPIRED": return P4ValidationStatus.SLA_EXPIRED;
    default: return P4ValidationStatus.PENDING;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapRiskClass(raw: string): RiskClassification {
  switch (raw) {
    case "LOW": return RiskClassification.LOW;
    case "STANDARD": return RiskClassification.STANDARD;
    case "HIGH": return RiskClassification.HIGH;
    default: return RiskClassification.STANDARD;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapScoringTrigger(raw: string): ScoringTrigger {
  switch (raw) {
    case "ONBOARDING": return ScoringTrigger.ONBOARDING;
    case "ICR_CHANGE": return ScoringTrigger.ICR_CHANGE;
    case "SCREENING_HIT": return ScoringTrigger.SCREENING_HIT;
    case "BATCH": return ScoringTrigger.BATCH;
    case "MANUAL": return ScoringTrigger.MANUAL;
    case "ERROR": return ScoringTrigger.ERROR;
    default: return ScoringTrigger.MANUAL;
  }
}

export function mapP4ValidationDtoToEntity(dto: P4ValidationItemDto): P4ValidationItem {
  return {
    validationId: dto.validationId,
    businessKey: dto.businessKey,
    oldRiskClass: mapRiskClass(dto.oldRiskClass),
    newRiskClass: mapRiskClass(dto.newRiskClass),
    oldScore: dto.oldScore,
    newScore: dto.newScore,
    scoreDelta: dto.scoreDelta,
    triggerReason: dto.triggerReason,
    sourceTrigger: mapScoringTrigger(dto.sourceTrigger),
    status: mapP4Status(dto.status),
    slaDeadline: dto.slaDeadline,
    slaExpired: dto.slaExpired,
    flaggedForDirectionRisques: dto.flaggedForDirectionRisques,
    createdAt: dto.createdAt,
  };
}

export function mapP4PendingCountDto(dto: P4PendingCountDto): P4PendingCount {
  return { count: typeof dto === "number" ? dto : (dto?.count ?? 0) };
}
